import React, { useEffect, useState, useRef } from 'react';
import { Empty } from 'antd';
import { makeStyles } from '@material-ui/styles';
import { useTranslation } from 'react-i18next';

import event from '~helpers/mixpanel';
import SpinLoading from 'common/components/spin-loading';
import NewSearch from 'common/components/search/new-search';
import CardGroup from './card';
import BookingTables from './booking-tables';
import useTableFilters from './useTableFilters';
import classNames from 'classnames';

const useStyles = makeStyles(theme => ({
  container: {
    height: 'calc(100vh - 50px)',
    overflowY: 'auto',
  },
  header: {
    position: 'sticky',
    top: '-1px',
    display: 'flex',
    padding: 24,
    zIndex: 100,
    transition: 'all 0.2s ease-in-out',

    '& h2': {
      ...theme.typography.h2,
      color: theme.colors.titleColor,
      marginBottom: 6,
      marginRight: 12,
    },
    '& h4': {
      fontSize: 14,
      lineHeight: '22px',
      color: theme.colors.captionColor,
      marginBottom: 0,
    },
  },
  stickyHeader: {
    backgroundColor: theme.colors.gray1,
    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)',
  },
  search: {
    '&>button': {
      borderRadius: 2,
      padding: '5px 16.8px',
      color: '#38b2a6',
      fontFamily: theme.typography.fontFamilySmall,
      backgroundColor: theme.colors.gray1,
      boxShadow: '0px 2px 8px rgba(56, 178, 166, 0.16)',

      '&:hover': {
        color: '#38b2a6',
        backgroundColor: theme.colors.gray1,
        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.16)',
      },

      '&>span:first-child': {
        marginRight: 8.8,
      },
    },
  },
  cardContainer: {
    padding: '0 24px',
  },
  tableContainer: {
    padding: 24,
  },
  loading: {
    height: '80vh',
  },
  empty: {
    marginTop: '10%',
  },
}));

export default function BookingList() {
  const { t } = useTranslation();
  const classes = useStyles();
  const {
    selectedProperties,
    setSelectedProperties,
    statisticsLoading,
    statisticsData,
    propertyNameOptions,
    bookingType,
    setBookingType,
    bookingStage,
    setBookingStage,
    otherTableFilter,
    setOtherTableFilter,
    tableData,
    tableLoading,
    refetchTableData,
    pendingOrderCount,
  } = useTableFilters();

  const [isSticked, setIsSticked] = useState(false);
  const bookingHeaderRef = useRef(null);

  const isAllProperty =
    selectedProperties.length === 0 || selectedProperties.length === propertyNameOptions.length;
  const newSearchTitleKey = isAllProperty
    ? t('landlord.long_tail.booking.list.all_property')
    : t('landlord.long_tail.booking.list.property_names');

  useEffect(() => {
    const el = bookingHeaderRef.current;

    if (el) {
      const observer = new IntersectionObserver(([e]) => setIsSticked(e.intersectionRatio < 1), {
        threshold: [1],
      });

      observer.observe(el);

      return () => observer.unobserve(el);
    }
  }, []);

  useEffect(() => {
    event('Viewed Booking List Page');
  }, []);

  return (
    <div className={classes.container} id="bookings--container">
      <hgroup
        className={classNames(classes.header, {
          [classes.stickyHeader]: isSticked,
        })}
        ref={bookingHeaderRef}
      >
        <h2>{t('landlord.long_tail.booking.list.title')}</h2>
        <NewSearch
          prefixIcon="home"
          hasArrow={true}
          hasBorder={false}
          hasNum={!isAllProperty}
          className={classes.search}
          options={propertyNameOptions}
          onChange={setSelectedProperties}
          placeholderInfo={{
            title: newSearchTitleKey,
            filteredTitle: newSearchTitleKey,
          }}
          defaultCheckedFilters={selectedProperties}
        />
      </hgroup>
      <Choose>
        <When condition={statisticsLoading}>
          <SpinLoading className={classes.loading} />
        </When>
        <When
          condition={
            statisticsData?.company?.herculesAllOrders?.totalCount > 0 ||
            statisticsData?.company?.manualAllBookings?.totalCount > 0 ||
            statisticsData?.company?.instantAllOrders?.totalCount > 0
          }
        >
          <CardGroup
            className={classes.cardContainer}
            loading={statisticsLoading}
            data={statisticsData}
          />
          <BookingTables
            className={classes.tableContainer}
            bookingType={bookingType}
            setBookingType={setBookingType}
            bookingStage={bookingStage}
            setBookingStage={setBookingStage}
            otherTableFilter={otherTableFilter}
            setOtherTableFilter={setOtherTableFilter}
            tableData={tableData}
            tableLoading={tableLoading}
            refetchTableData={refetchTableData}
            pendingOrderCount={pendingOrderCount}
            statisticsData={statisticsData}
          />
        </When>
        <Otherwise>
          <Empty className={classes.empty} description={t('no_any_booking_yet')} />
        </Otherwise>
      </Choose>
    </div>
  );
}
